import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftInput from "components/SoftInput";
import SoftButton from "components/SoftButton";
import SoftSnackbar from "components/SoftSnackbar";
import { Box, Card, CircularProgress, Collapse, IconButton } from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { API_URL } from "config";

const emptyScopeState = {
  branch: { all: false, ids: [] },
  sub_branch: { all: false, ids: [] },
  shift: { all: false, ids: [] },
};

const memberTypeForActor = (type) => {
  if (type === "client") return "client_contact";
  if (type === "vendor") return "vendor_contact";
  return "user";
};

const authHeaders = (token, extra = {}) => ({
  Accept: "application/json",
  "X-Requested-With": "XMLHttpRequest",
  Authorization: `Bearer ${token}`,
  ...extra,
});

const AddRole = () => {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [actorType, setActorType] = useState("internal");
  const [parentRoleId, setParentRoleId] = useState("");
  const [showInProjectManagementDropdown, setShowInProjectManagementDropdown] = useState(false);
  const [parentRoles, setParentRoles] = useState([]);
  const [permissionTree, setPermissionTree] = useState([]);
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [scopeOptions, setScopeOptions] = useState({});
  const [scopeState, setScopeState] = useState(emptyScopeState);
  const [userScope, setUserScope] = useState("self");
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [expanded, setExpanded] = useState([]);
  const [pageLoading, setPageLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", color: "success" });

  const storedPermissions = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("permissions") || "[]");
    } catch {
      return [];
    }
  }, []);

  const actorScopes = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("accessScopes") || "{}");
    } catch {
      return {};
    }
  }, []);

  const canAssignPermissions = storedPermissions.includes("access.roles.assign_permissions");
  const canAssignScopes = storedPermissions.includes("access.roles.assign_scopes");
  const canAssignMembers = storedPermissions.includes("access.roles.assign_members");
  const canViewRoles = storedPermissions.includes("access.roles.view");
  const canManageProjectManagementDropdownFlag =
    storedPermissions.includes("access.system.full_access");

  const hasWildcard = (scopeType) =>
    (actorScopes["*"] || []).includes("*") || (actorScopes[scopeType] || []).includes("*");

  const scopeList = (type) => scopeOptions[type] || [];
  const selectedBranchIds = scopeState.branch.ids.map(String);
  const selectedSubBranchIds = scopeState.sub_branch.ids.map(String);

  const parentRoleOptions = useMemo(
    () => parentRoles,
    [parentRoles]
  );

  const filteredSubBranches = useMemo(() => {
    if (scopeState.branch.all || selectedBranchIds.length === 0) return scopeList("sub_branch");
    return scopeList("sub_branch").filter((row) => selectedBranchIds.includes(String(row.branch_id)));
  }, [scopeOptions, scopeState.branch.all, selectedBranchIds.join(",")]);

  const filteredShifts = useMemo(() => {
    const shifts = scopeList("shift");
    if (!scopeState.sub_branch.all && selectedSubBranchIds.length > 0) {
      return shifts.filter((row) => selectedSubBranchIds.includes(String(row.subbranch_id)));
    }
    if (!scopeState.branch.all && selectedBranchIds.length > 0) {
      return shifts.filter((row) => selectedBranchIds.includes(String(row.branch_id)));
    }
    return shifts;
  }, [
    scopeOptions,
    scopeState.branch.all,
    scopeState.sub_branch.all,
    selectedBranchIds.join(","),
    selectedSubBranchIds.join(","),
  ]);

  const filteredUsers = useMemo(() => {
    const users = scopeOptions.users || [];

    return users.filter((user) => {
      if (!scopeState.branch.all && selectedBranchIds.length > 0 && !selectedBranchIds.includes(String(user.branch_id))) {
        return false;
      }
      if (
        !scopeState.sub_branch.all &&
        selectedSubBranchIds.length > 0 &&
        !selectedSubBranchIds.includes(String(user.subbranch_id))
      ) {
        return false;
      }
      if (!scopeState.shift.all && scopeState.shift.ids.length > 0) {
        const shiftSet = new Set(scopeState.shift.ids.map(String));
        return (user.shift_ids || []).some((shiftId) => shiftSet.has(String(shiftId)));
      }
      return true;
    });
  }, [
    scopeOptions,
    scopeState.branch.all,
    scopeState.sub_branch.all,
    scopeState.shift.all,
    scopeState.shift.ids.join(","),
    selectedBranchIds.join(","),
    selectedSubBranchIds.join(","),
  ]);

  const currentMemberType = memberTypeForActor(actorType);
  const memberOptions = useMemo(() => {
    if (actorType === "client") return scopeOptions.client_contacts || [];
    if (actorType === "vendor") return scopeOptions.vendor_contacts || [];
    return filteredUsers;
  }, [actorType, filteredUsers, scopeOptions.client_contacts, scopeOptions.vendor_contacts]);

  const memberTitle = actorType === "client"
    ? "Client Contacts"
    : actorType === "vendor"
      ? "Vendor Contacts"
      : "Users";

  useEffect(() => {
    const token = localStorage.getItem("authToken");
    const controller = new AbortController();

    const loadData = async () => {
      try {
        const requests = [];

        requests.push(
          canAssignPermissions
            ? fetch(`${API_URL}/access-permissions?assignable=1&tree=1`, {
                headers: authHeaders(token),
                signal: controller.signal,
              })
            : Promise.resolve({ ok: true, json: async () => ({ data: [] }) })
        );

        requests.push(
          canAssignScopes || canAssignMembers
            ? fetch(`${API_URL}/access-scope-options`, {
                headers: authHeaders(token),
                signal: controller.signal,
              })
            : Promise.resolve({ ok: true, json: async () => ({ data: {} }) })
        );

        requests.push(
          canViewRoles
            ? fetch(`${API_URL}/access-roles?per_page=500&parent_options=1`, {
                headers: authHeaders(token),
                signal: controller.signal,
              })
            : Promise.resolve({ ok: true, json: async () => ({ roles: { data: [] } }) })
        );

        const [permissionRes, scopeRes, rolesRes] = await Promise.all(requests);
        if (!permissionRes.ok || !scopeRes.ok || !rolesRes.ok) throw new Error("Failed to load role setup data");

        const permissionJson = await permissionRes.json();
        const scopeJson = await scopeRes.json();
        const rolesJson = await rolesRes.json();
        const roleRows = rolesJson.roles?.data || rolesJson.roles || rolesJson.data || [];

        setPermissionTree(permissionJson.data || []);
        setScopeOptions(scopeJson.data || {});
        setParentRoles(Array.isArray(roleRows) ? roleRows : []);
      } catch (error) {
        if (error.name !== "AbortError") {
          setSnackbar({ open: true, message: error.message, color: "error" });
        }
      } finally {
        setPageLoading(false);
      }
    };

    loadData();
    return () => controller.abort();
  }, [canAssignPermissions, canAssignScopes, canAssignMembers, canViewRoles]);

  useEffect(() => {
    if (parentRoleId && !parentRoleOptions.some((role) => String(role.id) === String(parentRoleId))) {
      setParentRoleId("");
    }
  }, [parentRoleId, parentRoleOptions]);

  useEffect(() => {
    if (
      (!canManageProjectManagementDropdownFlag || actorType !== "internal") &&
      showInProjectManagementDropdown
    ) {
      setShowInProjectManagementDropdown(false);
    }
  }, [actorType, canManageProjectManagementDropdownFlag, showInProjectManagementDropdown]);

  const collectDescendantIds = (node) =>
    (node.children || []).reduce((ids, child) => [...ids, child.id, ...collectDescendantIds(child)], []);

  const toggleExpand = (permissionId) => {
    setExpanded((prev) =>
      prev.includes(permissionId) ? prev.filter((item) => item !== permissionId) : [...prev, permissionId]
    );
  };

  const togglePermission = (node) => {
    const ids = [node.id, ...collectDescendantIds(node)];

    setSelectedPermissions((prev) => {
      const selected = new Set(prev);
      const shouldRemove = ids.every((permissionId) => selected.has(permissionId));

      ids.forEach((permissionId) => {
        if (shouldRemove) selected.delete(permissionId);
        else selected.add(permissionId);
      });

      return Array.from(selected);
    });
  };

  const toggleScopeId = (scopeType, scopeId) => {
    const idValue = String(scopeId);
    setScopeState((prev) => {
      const ids = prev[scopeType].ids.includes(idValue)
        ? prev[scopeType].ids.filter((item) => item !== idValue)
        : [...prev[scopeType].ids, idValue];

      return {
        ...prev,
        [scopeType]: { ...prev[scopeType], all: false, ids },
      };
    });
  };

  const setScopeAll = (scopeType, checked) => {
    setScopeState((prev) => ({
      ...prev,
      [scopeType]: { all: checked, ids: checked ? [] : prev[scopeType].ids },
    }));
  };

  const toggleMember = (memberId) => {
    const member = String(memberId);
    setSelectedMembers((prev) =>
      prev.includes(member) ? prev.filter((item) => item !== member) : [...prev, member]
    );
  };

  const rowsForScope = (scopeType, options) => {
    const state = scopeState[scopeType];
    if (state.all) {
      if (hasWildcard(scopeType)) {
        return [{ scope_type: scopeType, scope_id: null }];
      }
      return options.map((option) => ({ scope_type: scopeType, scope_id: Number(option.id) }));
    }

    return state.ids.map((scopeId) => ({ scope_type: scopeType, scope_id: Number(scopeId) }));
  };

  const scopesPayload = () => [
    ...rowsForScope("branch", scopeList("branch")),
    ...rowsForScope("sub_branch", filteredSubBranches),
    ...rowsForScope("shift", filteredShifts),
  ];

  const membersPayload = () =>
    selectedMembers.map((memberId) => ({
      member_type: currentMemberType,
      member_id: Number(memberId),
    }));

  const renderPermissions = (nodes, level = 0) =>
    (nodes || []).map((permission) => {
      const hasChildren = (permission.children || []).length > 0;
      const isOpen = expanded.includes(permission.id);
      const checked = selectedPermissions.includes(permission.id);
      const childIds = collectDescendantIds(permission);
      const checkedChildCount = childIds.filter((permissionId) => selectedPermissions.includes(permissionId)).length;
      const partial = !checked && checkedChildCount > 0;

      return (
        <React.Fragment key={permission.id}>
          <SoftBox pl={level * 3} py={0.75} display="flex" alignItems="center" sx={{ userSelect: "none" }}>
            {hasChildren ? (
              <IconButton size="small" onClick={() => toggleExpand(permission.id)}>
                <ChevronRightIcon
                  fontSize="small"
                  sx={{ transform: isOpen ? "rotate(90deg)" : "rotate(0deg)", transition: "0.2s" }}
                />
              </IconButton>
            ) : (
              <Box width={40} />
            )}

            <input
              type="checkbox"
              checked={checked}
              onChange={() => togglePermission(permission)}
              ref={(input) => {
                if (input) input.indeterminate = partial;
              }}
              style={{ marginRight: 8 }}
            />

            <SoftTypography variant="body2" fontWeight={level === 0 ? 600 : 400}>
              {permission.name}
            </SoftTypography>
          </SoftBox>

          {hasChildren && (
            <Collapse in={isOpen} timeout="auto" unmountOnExit>
              {renderPermissions(permission.children, level + 1)}
            </Collapse>
          )}
        </React.Fragment>
      );
    });

  const renderScopePicker = (scopeType, title, options) => (
    <SoftBox mb={3}>
      <SoftBox display="flex" alignItems="center" gap={1} mb={1}>
        <input
          type="checkbox"
          checked={scopeState[scopeType].all}
          onChange={(event) => setScopeAll(scopeType, event.target.checked)}
        />
        <SoftTypography variant="button" fontWeight="bold">
          All {title}
        </SoftTypography>
      </SoftBox>

      {!scopeState[scopeType].all && (
        <SoftBox display="flex" flexWrap="wrap" gap={1}>
          {options.length ? (
            options.map((option) => (
              <label key={`${scopeType}-${option.id}`} style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 220 }}>
                <input
                  type="checkbox"
                  checked={scopeState[scopeType].ids.includes(String(option.id))}
                  onChange={() => toggleScopeId(scopeType, option.id)}
                />
                <span style={{ fontSize: 13 }}>{option.name}</span>
              </label>
            ))
          ) : (
            <SoftTypography variant="caption" color="text">
              No {title.toLowerCase()} available.
            </SoftTypography>
          )}
        </SoftBox>
      )}
    </SoftBox>
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      setSnackbar({ open: true, message: "Please enter role name", color: "error" });
      return;
    }

    try {
      setSaving(true);

      const token = localStorage.getItem("authToken");
      const res = await fetch(`${API_URL}/access-roles`, {
        method: "POST",
        headers: authHeaders(token, { "Content-Type": "application/json" }),
        body: JSON.stringify({
          name: name.trim(),
          actor_type: actorType,
          parent_role_id: parentRoleId ? Number(parentRoleId) : null,
          user_scope: canAssignScopes && actorType === "internal" ? userScope : "self",
          ...(canManageProjectManagementDropdownFlag
            ? {
                show_in_project_management_dropdown:
                  actorType === "internal" && showInProjectManagementDropdown,
              }
            : {}),
          permissions: canAssignPermissions ? selectedPermissions : [],
          scopes: canAssignScopes && actorType === "internal" ? scopesPayload() : [],
          members: canAssignMembers ? membersPayload() : [],
        }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.message || Object.values(data.errors || {})?.[0]?.[0] || "Failed to create role");
      }

      setSnackbar({ open: true, message: "Role created successfully", color: "success" });
      setTimeout(() => navigate("/roles/list"), 1000);
    } catch (error) {
      setSaving(false);
      setSnackbar({ open: true, message: error.message, color: "error" });
    }
  };

  if (pageLoading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <SoftBox py={10} display="flex" justifyContent="center">
          <CircularProgress />
        </SoftBox>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <SoftSnackbar
        color={snackbar.color}
        icon="notifications"
        title="Role"
        content={snackbar.message}
        open={snackbar.open}
        close={() => setSnackbar({ ...snackbar, open: false })}
        time={2500}
      />

      <SoftBox p={3}>
        <SoftTypography variant="h5" mb={2}>
          Create Role
        </SoftTypography>

        <form onSubmit={handleSubmit}>
          <SoftBox display="flex" flexWrap="wrap" gap={2} mb={3}>
            <SoftBox sx={{ width: { xs: "100%", sm: 320 } }}>
              <SoftTypography variant="caption" fontWeight="bold">
                Role Name
              </SoftTypography>
              <SoftInput
                placeholder="Role Name"
                value={name}
                size="small"
                onChange={(e) => setName(e.target.value)}
                fullWidth
                autoFocus
                disabled={saving}
              />
            </SoftBox>

            <SoftBox sx={{ width: { xs: "100%", sm: 220 } }}>
              <SoftTypography variant="caption" fontWeight="bold">
                Role Type
              </SoftTypography>
              <select
                value={actorType}
                onChange={(e) => {
                  setActorType(e.target.value);
                  setSelectedMembers([]);
                }}
                disabled={saving}
                style={{ width: "100%", padding: 8, border: "1px solid #d2d6da", borderRadius: 6 }}
              >
                <option value="internal">Internal</option>
                <option value="client">Client</option>
                <option value="vendor">Vendor</option>
                <option value="all">All</option>
              </select>
            </SoftBox>

            <SoftBox sx={{ width: { xs: "100%", sm: 260 } }}>
              <SoftTypography variant="caption" fontWeight="bold">
                Upper Role
              </SoftTypography>
              <select
                value={parentRoleId}
                onChange={(e) => setParentRoleId(e.target.value)}
                disabled={saving || !canViewRoles}
                style={{ width: "100%", padding: 8, border: "1px solid #d2d6da", borderRadius: 6 }}
              >
                <option value="">No Upper Role</option>
                {parentRoleOptions.map((role) => (
                  <option key={role.id} value={role.id}>
                    {role.name}
                  </option>
                ))}
              </select>
            </SoftBox>

            {canManageProjectManagementDropdownFlag && (
              <SoftBox
                sx={{
                  width: { xs: "100%", sm: 380 },
                  display: "flex",
                  alignItems: "flex-end",
                  pb: 0.5,
                }}
              >
                <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}>
                  <input
                    type="checkbox"
                    checked={actorType === "internal" && showInProjectManagementDropdown}
                    disabled={saving || actorType !== "internal"}
                    onChange={(event) => setShowInProjectManagementDropdown(event.target.checked)}
                  />
                  <span>Show this role in Project Management dropdown on bids page</span>
                </label>
              </SoftBox>
            )}
          </SoftBox>

          {canAssignPermissions && (
            <Card sx={{ p: 2, mb: 3 }}>
              <SoftTypography variant="h6" mb={1}>
                Permissions
              </SoftTypography>
              {permissionTree.length ? (
                renderPermissions(permissionTree)
              ) : (
                <SoftTypography variant="caption" color="text">
                  No assignable permissions available.
                </SoftTypography>
              )}
            </Card>
          )}

          {canAssignScopes && actorType === "internal" && (
            <Card sx={{ p: 2, mb: 3 }}>
              <SoftTypography variant="h6" mb={2}>
                Internal Data Access
              </SoftTypography>

              <SoftBox mb={3}>
                <SoftTypography variant="button" fontWeight="bold" display="block" mb={1}>
                  User Access
                </SoftTypography>
                <select
                  value={userScope}
                  onChange={(event) => setUserScope(event.target.value)}
                  style={{ width: 260, padding: 8, border: "1px solid #d2d6da", borderRadius: 6 }}
                >
                  <option value="self">Self</option>
                  <option value="all">All Users In Selected Scope</option>
                </select>
              </SoftBox>

              {renderScopePicker("branch", "Branches", scopeList("branch"))}
              {renderScopePicker("sub_branch", "Sub Branches", filteredSubBranches)}
              {renderScopePicker("shift", "Shifts", filteredShifts)}
            </Card>
          )}

          {canAssignMembers && (
            <Card sx={{ p: 2, mb: 3 }}>
              <SoftTypography variant="h6" mb={1}>
                Assign Role To {memberTitle}
              </SoftTypography>
              <SoftBox display="flex" flexWrap="wrap" gap={1}>
                {memberOptions.length ? (
                  memberOptions.map((option) => (
                    <label key={option.id} style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 220 }}>
                      <input
                        type="checkbox"
                        checked={selectedMembers.includes(String(option.id))}
                        onChange={() => toggleMember(option.id)}
                      />
                      <span style={{ fontSize: 13 }}>{option.name}</span>
                    </label>
                  ))
                ) : (
                  <SoftTypography variant="caption" color="text">
                    No {memberTitle.toLowerCase()} available in your access scope.
                  </SoftTypography>
                )}
              </SoftBox>
            </Card>
          )}

          <SoftBox display="flex" gap={2}>
            <SoftButton type="submit" color="info" disabled={saving}>
              {saving ? "Creating..." : "Create Role"}
            </SoftButton>

            <SoftButton color="info" onClick={() => navigate("/roles/list")} disabled={saving}>
              Cancel
            </SoftButton>
          </SoftBox>
        </form>
      </SoftBox>
    </DashboardLayout>
  );
};

export default AddRole;
